import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Link as LinkIcon, Plus, Trash2, X } from 'lucide-react';
import type { BusinessLink, BusinessProfileInput } from '../../shared/types';
import { normalizeHashtag } from '../../shared/utils';
import { PageHeader } from '../components/layout';
import { Alert, Badge, Button, Field, Input, Skeleton, Textarea, useToast } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { useWorkspaceStore } from '../stores/workspace';
import { cn } from '../lib/utils';

interface FormState {
  description: string;
  industry: string;
  audience: string;
  toneDefault: string;
  productsServices: string;
  website: string;
  phone: string;
  address: string;
  defaultHashtags: string[];
  links: BusinessLink[];
  extraContext: string;
}

const EMPTY_FORM: FormState = {
  description: '',
  industry: '',
  audience: '',
  toneDefault: '',
  productsServices: '',
  website: '',
  phone: '',
  address: '',
  defaultHashtags: [],
  links: [],
  extraContext: '',
};

const TONE_PRESETS = [
  'Warm and conversational',
  'Confident and direct',
  'Playful and witty',
  'Expert and measured',
] as const;

/** Trims and drops empty rows so a blank "Add link" row never counts as a change. */
function buildPayload(form: FormState): BusinessProfileInput {
  return {
    description: form.description.trim() || null,
    industry: form.industry.trim() || null,
    audience: form.audience.trim() || null,
    toneDefault: form.toneDefault.trim() || null,
    productsServices: form.productsServices.trim() || null,
    website: form.website.trim() || null,
    phone: form.phone.trim() || null,
    address: form.address.trim() || null,
    defaultHashtags: form.defaultHashtags,
    links: form.links
      .map((link) => ({ label: link.label.trim(), url: link.url.trim() }))
      .filter((link) => link.label || link.url),
    extraContext: form.extraContext.trim() || null,
  };
}

/* ================================================================== */
/* Hashtag tag-input                                                   */
/* ================================================================== */

function HashtagInput({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');

  const commit = (raw: string) => {
    const tag = normalizeHashtag(raw);
    if (!tag) return;
    if (value.some((existing) => existing.toLowerCase() === tag.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...value, tag]);
    setDraft('');
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1.5 focus-within:border-accent focus-within:ring-2 focus-within:ring-ring/35">
      {value.map((tag) => (
        <Badge key={tag} tone="accent" size="md" className="gap-1 pr-1">
          {tag}
          <button
            type="button"
            aria-label={`Remove ${tag}`}
            onClick={() => onChange(value.filter((item) => item !== tag))}
            className="rounded-full p-0.5 hover:bg-accent/20"
          >
            <X className="size-2.5" />
          </button>
        </Badge>
      ))}
      <input
        value={draft}
        onChange={(event) => {
          const next = event.target.value;
          if (/[\s,]$/.test(next)) {
            commit(next);
          } else {
            setDraft(next);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit(draft);
          } else if (event.key === 'Backspace' && !draft && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => commit(draft)}
        placeholder={value.length ? 'Add another…' : '#specialtycoffee #austin'}
        className="min-w-32 flex-1 border-0 bg-transparent p-1 text-sm text-fg outline-none placeholder:text-subtle"
      />
    </div>
  );
}

/* ================================================================== */
/* Links editor                                                        */
/* ================================================================== */

function LinksEditor({
  value,
  onChange,
}: {
  value: BusinessLink[];
  onChange: (next: BusinessLink[]) => void;
}) {
  const update = (index: number, patch: Partial<BusinessLink>) =>
    onChange(value.map((link, i) => (i === index ? { ...link, ...patch } : link)));

  return (
    <div className="space-y-2">
      {value.map((link, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            aria-label="Link label"
            placeholder="Menu"
            inputSize="sm"
            value={link.label}
            onChange={(event) => update(index, { label: event.target.value })}
            className="w-36 shrink-0"
          />
          <Input
            aria-label="Link URL"
            placeholder="https://example.com/menu"
            inputSize="sm"
            leadingIcon={<LinkIcon />}
            wrapperClassName="min-w-0 flex-1"
            value={link.url}
            onChange={(event) => update(index, { url: event.target.value })}
          />
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Remove link"
            onClick={() => onChange(value.filter((_, i) => i !== index))}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      ))}
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<Plus className="size-3.5" />}
        onClick={() => onChange([...value, { label: '', url: '' }])}
      >
        Add link
      </Button>
    </div>
  );
}

/* ================================================================== */
/* Page                                                                 */
/* ================================================================== */

/**
 * Brand — owned by WEB-4. Business profile form: description, industry,
 * audience, tone, products/services, contact details, default hashtags and
 * links, plus free-form context. Autosaves on blur, with a save bar as a
 * fallback for non-blur changes (tags, links, discarding).
 */
export function BrandPage() {
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const query = useQuery({
    queryKey: qk.businessProfile(workspaceId),
    queryFn: () => api.workspaces.businessProfile(workspaceId as string),
    enabled: Boolean(workspaceId),
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const savedFormRef = useRef<FormState>(EMPTY_FORM);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const blurTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!query.data || loadedOnce) return;
    const profile = query.data.profile;
    const next: FormState = profile
      ? {
          description: profile.description ?? '',
          industry: profile.industry ?? '',
          audience: profile.audience ?? '',
          toneDefault: profile.toneDefault ?? '',
          productsServices: profile.productsServices ?? '',
          website: profile.website ?? '',
          phone: profile.phone ?? '',
          address: profile.address ?? '',
          defaultHashtags: profile.defaultHashtags ?? [],
          links: profile.links ?? [],
          extraContext: profile.extraContext ?? '',
        }
      : EMPTY_FORM;
    setForm(next);
    savedFormRef.current = next;
    setLoadedOnce(true);
  }, [query.data, loadedOnce]);

  const save = useMutation({
    mutationFn: (body: BusinessProfileInput) =>
      api.workspaces.saveBusinessProfile(workspaceId as string, body),
    onSuccess: (response) => {
      const profile = response.profile;
      const next: FormState = {
        description: profile.description ?? '',
        industry: profile.industry ?? '',
        audience: profile.audience ?? '',
        toneDefault: profile.toneDefault ?? '',
        productsServices: profile.productsServices ?? '',
        website: profile.website ?? '',
        phone: profile.phone ?? '',
        address: profile.address ?? '',
        defaultHashtags: profile.defaultHashtags ?? [],
        links: profile.links ?? [],
        extraContext: profile.extraContext ?? '',
      };
      savedFormRef.current = next;
      setForm(next);
      void queryClient.invalidateQueries({ queryKey: qk.businessProfile(workspaceId) });
    },
    onError: (error) => {
      toast({
        title: 'Could not save the business profile',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const dirty = useMemo(
    () => JSON.stringify(buildPayload(form)) !== JSON.stringify(buildPayload(savedFormRef.current)),
    [form],
  );

  const requestSave = () => {
    if (!workspaceId) return;
    save.mutate(buildPayload(form));
  };

  // Coalesces rapid blurs (tabbing across several fields) into one save call.
  const onFieldBlur = () => {
    window.clearTimeout(blurTimer.current);
    blurTimer.current = window.setTimeout(() => {
      if (JSON.stringify(buildPayload(form)) !== JSON.stringify(buildPayload(savedFormRef.current))) {
        requestSave();
      }
    }, 250);
  };

  useEffect(() => () => window.clearTimeout(blurTimer.current), []);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  if (!workspaceId) return null;

  return (
    <>
      <PageHeader
        title="Brand"
        description="What your business does, who it talks to, and how. This context goes into every AI draft."
      />

      {query.isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-2/3" />
        </div>
      ) : query.isError ? (
        <Alert tone="danger" title="Could not load the business profile">
          {query.error instanceof ApiError ? query.error.message : 'Try refreshing the page.'}
        </Alert>
      ) : (
        <div className="space-y-6 pb-20">
          <Field
            label="What does the business do?"
            htmlFor="brand-description"
            hint="Two or three sentences. Say what you sell and what makes it different."
          >
            <Textarea
              id="brand-description"
              rows={4}
              maxRows={10}
              placeholder="We roast small-batch coffee in Austin and ship it within 24 hours of roasting."
              value={form.description}
              onChange={(event) => update('description', event.target.value)}
              onBlur={onFieldBlur}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Industry" htmlFor="brand-industry">
              <Input
                id="brand-industry"
                placeholder="Specialty coffee retail"
                value={form.industry}
                onChange={(event) => update('industry', event.target.value)}
                onBlur={onFieldBlur}
              />
            </Field>
            <Field label="Website" htmlFor="brand-website">
              <Input
                id="brand-website"
                type="url"
                placeholder="https://northwind.coffee"
                value={form.website}
                onChange={(event) => update('website', event.target.value)}
                onBlur={onFieldBlur}
              />
            </Field>
          </div>

          <Field
            label="Who are you talking to?"
            htmlFor="brand-audience"
            hint="Your audience, in their words rather than a demographic bracket."
          >
            <Input
              id="brand-audience"
              placeholder="Home brewers who grind their own beans"
              value={form.audience}
              onChange={(event) => update('audience', event.target.value)}
              onBlur={onFieldBlur}
            />
          </Field>

          <Field label="Default tone" htmlFor="brand-tone">
            <Input
              id="brand-tone"
              placeholder="Warm and conversational"
              value={form.toneDefault}
              onChange={(event) => update('toneDefault', event.target.value)}
              onBlur={onFieldBlur}
            />
            <div className="flex flex-wrap gap-1.5 pt-2">
              {TONE_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => {
                    update('toneDefault', preset);
                    onFieldBlur();
                  }}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs transition-colors',
                    form.toneDefault === preset
                      ? 'border-accent bg-accent-soft text-accent'
                      : 'border-border text-muted hover:border-border-strong hover:text-fg',
                  )}
                >
                  {preset}
                </button>
              ))}
            </div>
          </Field>

          <Field
            label="Products & services"
            htmlFor="brand-products"
            hint="A short list — what you actually offer, so drafts can reference it specifically."
          >
            <Textarea
              id="brand-products"
              rows={3}
              maxRows={8}
              placeholder="Single-origin bags (12oz), espresso subscription, wholesale for cafes"
              value={form.productsServices}
              onChange={(event) => update('productsServices', event.target.value)}
              onBlur={onFieldBlur}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Phone" htmlFor="brand-phone">
              <Input
                id="brand-phone"
                type="tel"
                placeholder="(512) 555-0134"
                value={form.phone}
                onChange={(event) => update('phone', event.target.value)}
                onBlur={onFieldBlur}
              />
            </Field>
            <Field label="Address" htmlFor="brand-address">
              <Input
                id="brand-address"
                placeholder="1200 Congress Ave, Austin, TX"
                value={form.address}
                onChange={(event) => update('address', event.target.value)}
                onBlur={onFieldBlur}
              />
            </Field>
          </div>

          <Field
            label="Default hashtags"
            hint="Suggested on every draft, not forced — remove them per post whenever they don't fit."
          >
            <HashtagInput
              value={form.defaultHashtags}
              onChange={(next) => {
                update('defaultHashtags', next);
                requestSave();
              }}
            />
          </Field>

          <Field label="Links" hint="Menu, booking page, store locator — anything worth linking to from a post.">
            <LinksEditor
              value={form.links}
              onChange={(next) => update('links', next)}
            />
          </Field>

          <Field
            label="Anything else the AI should know"
            htmlFor="brand-extra"
            hint="House style rules, phrases to avoid, seasonal notes — free text."
          >
            <Textarea
              id="brand-extra"
              rows={3}
              maxRows={8}
              placeholder="Never use the word 'discount' — say 'offer' instead. Always mention we're women-owned."
              value={form.extraContext}
              onChange={(event) => update('extraContext', event.target.value)}
              onBlur={onFieldBlur}
            />
          </Field>
        </div>
      )}

      <AnimatePresence>
        {dirty && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.18 }}
            className="pap-pop pointer-events-auto fixed inset-x-4 bottom-4 z-40 mx-auto flex max-w-xl items-center justify-between gap-3 px-4 py-3 sm:inset-x-auto sm:right-8"
          >
            <p className="text-sm text-fg">You have unsaved changes to the brand profile.</p>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setForm(savedFormRef.current)}
                disabled={save.isPending}
              >
                Discard
              </Button>
              <Button size="sm" loading={save.isPending} onClick={requestSave}>
                Save changes
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default BrandPage;
